import { EditorState, StateField, StateEffect } from '@codemirror/state';
import { EditorView, ViewPlugin, ViewUpdate } from '@codemirror/view';
import { syntaxTree } from '@codemirror/language';

// * Context information that will be passed to Angular autocomplete service
export interface TypingContext {
  searchTerm: string;
  propertyName?: string;
  isPropertyValue: boolean;
  isPropertyName: boolean;
  cursorPosition: number;
  syntaxTreeContext: any;
  beforeMatch?: any;
  currentNode?: any;
}

// * Event that gets triggered when typing context changes
export interface ContextChangeEvent {
  context: TypingContext | null;
  shouldShowAutocomplete: boolean;
  editorView: EditorView;
}

// * Type for context change callback
export type ContextChangeCallback = (event: ContextChangeEvent) => void;

// * State effect for triggering context detection
export const triggerContextDetection = StateEffect.define<boolean>();

// * State field to track when context detection should occur
export const contextDetectionField = StateField.define<boolean>({
  create: () => false,
  update: (value, tr) => {
    for (let effect of tr.effects) {
      if (effect.is(triggerContextDetection)) {
        return effect.value;
      }
    }
    return value;
  },
});

export class ContextDetector {
  private callback: ContextChangeCallback | null = null;
  private getManualTriggerState: (() => boolean) | null = null;
  private getDropdownVisibilityState: (() => boolean) | null = null;

  constructor(
    callback?: ContextChangeCallback, // ! <- Calls QueryEditorComponent.onContextChange
    getManualTriggerState?: () => boolean,
    getDropdownVisibilityState?: () => boolean,
  ) {
    this.callback = callback || null;
    this.getManualTriggerState = getManualTriggerState || null;
    this.getDropdownVisibilityState = getDropdownVisibilityState || null;
  }

  // * Set the callback for context changes
  setCallback(callback: ContextChangeCallback): void {
    this.callback = callback;
  }

  // * NEW: Force manual context detection
  forceContextDetection(view: EditorView): void {
    const pos = view.state.selection.main.head;
    const context = this.analyzeContext(view, pos);
    const shouldShow = this.shouldShowAutocomplete(context);

    // * Trigger callback if available
    if (this.callback) {
      this.callback({
        context,
        shouldShowAutocomplete: shouldShow,
        editorView: view,
      });
    }
  }

  // * Analyze typing context based on cursor position and syntax tree
  // * This is extracted from the original myCompletions logic
  analyzeContext(view: EditorView, pos: number): TypingContext | null {
    const state = view.state;
    const tree = syntaxTree(state);
    const isManuallyTriggered = this.getManualTriggerState
      ? this.getManualTriggerState()
      : false;

    // * Get text before cursor for pattern matching
    const before = this.matchBefore(state, pos, /\S+/);
    const beforeWord = this.matchBefore(state, pos, /[\w_]+/);

    // * Resolve syntax tree Node at cursor position
    const current = tree.resolveInner(pos, -1);

    // console.log('🔍 Context analysis:', {
    //   pos,
    //   beforeText: before?.text || 'none',
    //   beforeWordText: beforeWord?.text || 'none',
    //   currentNodeType: current.type.name,
    //   isManuallyTriggered,
    // });

    const context: TypingContext = {
      searchTerm: '',
      isPropertyValue: false,
      isPropertyName: false,
      cursorPosition: pos,
      syntaxTreeContext: current,
      beforeMatch: before,
      currentNode: current,
    };

    // * CASE 1: User is at a property field (e.g., "source_country:")
    if (current.type.name === 'PropertyField') {
      context.isPropertyValue = true;
      context.propertyName = this.extractPropertyName(
        state,
        current.firstChild,
      );
      context.searchTerm = '';
      return context;
    }

    // * CASE 2: User is typing inside quoted or unquoted values
    if (current.type.name === 'UnQuoted' || current.type.name === 'Quoted') {
      const parent = current.parent;
      const grandParent = parent?.parent;
      const greatGrandParent = grandParent?.parent;

      // console.log('🔍 CASE 2 analysis:', {
      //   currentType: current.type.name,
      //   parentType: parent?.type.name,
      //   grandParentType: grandParent?.type.name,
      //   greatGrandParentType: greatGrandParent?.type.

      // * Proper hierarchy: Quoted/UnQuoted inside String > PropertyValue > PropertyField
      if (
        parent?.type.name === 'String' &&
        grandParent?.type.name === 'PropertyValue' &&
        greatGrandParent?.type.name === 'PropertyField'
      ) {
        context.isPropertyValue = true;
        context.propertyName = this.extractPropertyName(
          state,
          greatGrandParent.firstChild,
        );
        context.searchTerm = state.sliceDoc(current.from, current.to);
        // console.log(
        //   ':: CASE 2a: Property value - proper hierarchy:',
        //   context.propertyName,
        //   'search term:',
        //   context.searchTerm,
        // );
        return context;
      }

      // * Grammar issue: sometimes UnQuoted is not wrapped inside String or PropertyValue
      if (grandParent?.type.name === 'PropertyField') {
        context.isPropertyValue = true;
        context.propertyName = this.extractPropertyName(
          state,
          grandParent.firstChild,
        );
        context.searchTerm = state.sliceDoc(current.from, current.to);
        // console.log(
        //   ':: CASE 2b: Property value - simplified hierarchy:',
        //   context.propertyName,
        //   'search term:',
        //   context.searchTerm,
        // );
        return context;
      }
    }

    // * CASE 3: User is inside a property name
    if (current.type.name === 'PropertyName') {
      context.isPropertyValue = true;
      context.propertyName = this.extractPropertyName(state, current);
      context.searchTerm = '';
      // console.log(':: CASE 3: Inside property name:', context.propertyName);
      return context;
    }

    // * CASE 3.5: User is right after colon in property field (fallback)
    if (current.type.name === 'PropertyField') {
      // * Check if cursor is after the colon
      const doc = state.doc;
      const line = doc.lineAt(pos);
      const lineText = line.text;
      const cursorInLine = pos - line.from;

      // * Look for colon before cursor position
      let colonPos = -1;
      for (let i = cursorInLine - 1; i >= 0; i--) {
        if (lineText[i] === ':') {
          colonPos = i;
          break;
        }
        if (!/\s/.test(lineText[i])) break; // Stop if we hit non-whitespace
      }

      if (colonPos !== -1) {
        // * Extract text after colon
        const afterColon = lineText
          .substring(colonPos + 1, cursorInLine)
          .trim();
        context.isPropertyValue = true;
        context.propertyName = this.extractPropertyName(
          state,
          current.firstChild,
        );
        context.searchTerm = afterColon;
        // console.log(
        //   ':: CASE 3.5: Property field after colon:',
        //   context.propertyName,
        //   'search term:',
        //   context.searchTerm,
        // );
        return context;
      }
    }

    // * CASE 4: User is positioned after a logical operator - show all property names
    if (beforeWord && this.isLogicalOperator(beforeWord.text)) {
      context.isPropertyName = true;
      context.searchTerm = ''; // Empty search term to show all property names
      // console.log(
      //   ':: CASE 4: After logical operator:',
      //   beforeWord.text,
      //   '- showing all property names',
      // );
      return context;
    }

    // * CASE 5: User is typing a property name (not inside a value)
    if (
      beforeWord &&
      (current.type.name === 'UnQuoted' ||
        current.type.name === 'ParenExpr' ||
        current.type.name === 'Term')
    ) {
      context.isPropertyName = true;
      context.searchTerm = beforeWord.text;
      // console.log(':: CASE 5: Property name from beforeWord:', beforeWord.text);
      return context;
    }

    // * CASE 6: Manual trigger - special handling for empty context
    if (isManuallyTriggered) {
      // * Check if we have a valid word being typed
      if (beforeWord && this.isValidPropertyNameContext(beforeWord.text)) {
        context.isPropertyName = true;
        context.searchTerm = beforeWord.text;
        // console.log(
        //   ':: CASE 6a: Manual trigger with valid word:',
        //   beforeWord.text,
        // );
        return context;
      }

      // * Manual trigger with no word - show all property names
      context.isPropertyName = true;
      context.searchTerm = '';
      // console.log(
      //   ':: CASE 6b: Manual trigger with empty context - show all property names',
      // );
      return context;
    }

    // * CASE 7: Automatic mode - use before match (old behavior)
    if (before && this.isValidPropertyNameContext(before.text)) {
      context.isPropertyName = true;
      context.searchTerm = before.text;
      // console.log(
      //   ':: CASE 7: Automatic property name completion:',
      //   before.text,
      // );
      return context;
    }

    // console.log('--- No valid context found ---');
    return null;
  }

  // * Extract property name from syntax tree node
  private extractPropertyName(state: EditorState, propertyNode: any): string {
    if (!propertyNode) return '';

    const fullPropertyName = state.sliceDoc(propertyNode.from, propertyNode.to);
    return fullPropertyName.replace(':', '');
  }

  // * Match text before cursor position (extracted from original logic)
  private matchBefore(
    state: EditorState,
    pos: number,
    pattern: RegExp,
  ): { text: string; from: number; to: number } | null {
    const doc = state.doc;
    const line = doc.lineAt(pos);
    const lineStart = line.from;
    const textBefore = doc.sliceString(lineStart, pos);

    // * Find all matches and get the one closest to cursor (rightmost)
    const matches = Array.from(textBefore.matchAll(new RegExp(pattern, 'g')));
    if (matches.length === 0) return null;

    // * Get the last (rightmost) match
    const lastMatch = matches[matches.length - 1];
    const matchStart = lineStart + lastMatch.index!;
    const matchEnd = matchStart + lastMatch[0].length;

    return {
      text: lastMatch[0],
      from: matchStart,
      to: matchEnd,
    };
  }

  // * NEW: Check if text is a logical operator
  private isLogicalOperator(text: string): boolean {
    const operators = ['AND', 'OR', 'NOT'];
    return operators.includes(text.toUpperCase());
  }

  // * NEW: Validate if text is a valid property name context (not random punctuation or operators)
  private isValidPropertyNameContext(text: string): boolean {
    if (!text) return false;

    // * Reject logical operators - they indicate we should show all property names
    if (this.isLogicalOperator(text)) {
      // console.log(
      //   '🔍 Detected logical operator:',
      //   text,
      //   '- should show all property names',
      // );
      return false;
    }

    // * Only allow alphanumeric characters and underscores for property names
    // * Reject random punctuation like ")", "(", etc.
    const isValidPropertyName = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(text);

    // console.log(
    //   '🔍 Validating property name context:',
    //   text,
    //   'valid:',
    //   isValidPropertyName,
    // );
    return isValidPropertyName;
  }

  // * Determine if autocomplete should be shown based on context
  // * STEP 5 FIX: Support continued typing after manual trigger
  private shouldShowAutocomplete(context: TypingContext | null): boolean {
    if (!context) {
      return false;
    }

    // * Check current states
    const isManuallyTriggered = this.getManualTriggerState
      ? this.getManualTriggerState()
      : false;
    const isDropdownVisible = this.getDropdownVisibilityState
      ? this.getDropdownVisibilityState()
      : false;

    // console.log(
    //   '🔍 shouldShowAutocomplete - context:',
    //   context.isPropertyValue ? 'property value' : 'property name',
    //   'manually triggered:',
    //   isManuallyTriggered,
    //   'dropdown visible:',
    //   isDropdownVisible,
    //   'search term:',
    //   `"${context.searchTerm}"`,
    // );

    // * STEP 5 FIX: Allow continued typing if dropdown is already visible
    if (isDropdownVisible) {
      // console.log(
      //   '✅ Dropdown already visible - allowing updates while typing',
      // );

      // * Validate context is still appropriate for autocomplete
      if (context.isPropertyValue && context.propertyName) {
        // console.log('✅ Property value context - continuing');
        return true;
      }

      if (context.isPropertyName) {
        // console.log('✅ Property name context - continuing');
        return true;
      }

      // console.log('❌ Context no longer valid - hiding dropdown');
      return false;
    }

    // * NEW: Only show autocomplete when manually triggered (for initial opening)
    if (!isManuallyTriggered) {
      // console.log('❌ Not manually triggered - hiding autocomplete');
      return false;
    }

    // * MANUAL TRIGGER ACTIVE - Check context validity for initial opening

    // * CASE 1: Property value completion - only when we have a property name
    if (context.isPropertyValue && context.propertyName) {
      // console.log('✅ Property value context - valid for manual trigger');
      return true;
    }

    // * CASE 2: Property name completion - always show when manually triggered
    if (context.isPropertyName) {
      // console.log('✅ Property name context - valid for manual trigger');
      return true;
    }

    // console.log('❌ No valid context for autocomplete');
    return false;
  }

  // * Create the ViewPlugin for context detection
  createPlugin(): ViewPlugin<any> {
    const detector = this;

    return ViewPlugin.fromClass(
      class {
        constructor(view: EditorView) {
          // * Initial context detection
          this.detectContext(view);
        }

        update(update: ViewUpdate) {
          // * Detect context on document changes or selection changes
          if (update.docChanged || update.selectionSet) {
            this.detectContext(update.view);
          }
        }

        private detectContext(view: EditorView) {
          const pos = view.state.selection.main.head;
          const context = detector.analyzeContext(view, pos);
          const shouldShow = detector.shouldShowAutocomplete(context);

          // * Trigger callback if available
          if (detector.callback) {
            detector.callback({
              context,
              shouldShowAutocomplete: shouldShow,
              editorView: view,
            });
          }
        }
      },
    );
  }
}

// * Create a context detector extension for CodeMirror
export function createContextDetector(
  callback?: ContextChangeCallback,
  getManualTriggerState?: () => boolean,
  getDropdownVisibilityState?: () => boolean,
): [any, any, ContextDetector] {
  const detector = new ContextDetector(
    callback,
    getManualTriggerState,
    getDropdownVisibilityState,
  );

  // ! returns [field, plugin, detector]
  return [contextDetectionField, detector.createPlugin(), detector];
}
